import { useState, useEffect, useMemo } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { usePresence } from '../context/UserPresenceProvider';

/**
 * Hook to fetch and subscribe to user data for chat display
 * @param {string} userId - The ID of the user to fetch
 * @returns {Object} User data including displayName, photoURL, isOnline, etc.
 */
// FIX 3.3: TTL-bounded cache to prevent unbounded memory growth
const userCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 100;

const getCachedUser = (userId) => {
    const entry = userCache.get(userId);
    if (!entry) return null;
    if (Date.now() - entry.ts > CACHE_TTL) {
        userCache.delete(userId);
        return null;
    }
    return entry.data;
};

const setCachedUser = (userId, data) => {
    if (userCache.size >= MAX_CACHE_SIZE) {
        // Evict oldest entry
        const oldest = userCache.keys().next().value;
        userCache.delete(oldest);
    }
    userCache.set(userId, { data, ts: Date.now() });
};

const useChatUser = (userId) => {
    const { presenceByUserId } = usePresence();
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(false);

    // Get presence data if available
    const presence = presenceByUserId?.[userId];

    useEffect(() => {
        if (!userId) {
            setUserData(null);
            return;
        }

        let isMounted = true;

        const fetchUser = async () => {
            // Check TTL-bounded cache first
            const cached = getCachedUser(userId);
            if (cached) {
                if (isMounted) {
                    setUserData(cached);
                }
                return;
            }

            setLoading(true);
            try {
                const userDoc = await getDoc(doc(db, 'users', userId));
                if (userDoc.exists()) {
                    const data = userDoc.data();
                    setCachedUser(userId, data);
                    if (isMounted) {
                        setUserData(data);
                    }
                }
            } catch (error) {
                console.error('[useChatUser] Failed to fetch user:', userId, error);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchUser();

        return () => {
            isMounted = false;
        };
    }, [userId]);

    // FIX D5: Stabilize return value with useMemo to prevent unnecessary re-renders
    // ponytail: one line, native React feature, prevents cascading re-renders across communities
    return useMemo(() => {
      // Merge presence and fetched data
      // userData (from 'users' collection) takes precedence for profile info
      // Presence takes precedence for online status only
      const mergedUser = {
        uid: userId,
        // Spread presence first (for isOnline, lastActiveAt)
        ...presence,
        // Then spread userData to override with more complete profile info
        ...userData,
        // Explicitly set these fields with proper fallback chain
        displayName: userData?.displayName || userData?.username || userData?.name || presence?.displayName || presence?.username || 'User',
        photoURL: userData?.photoURL || presence?.photoURL || null,
        isOnline: presence?.isOnline || false,
      };
      
      return {
        user: mergedUser,
        loading,
        isOnline: mergedUser.isOnline
      };
    }, [userId, presence, userData, loading]);
};

export default useChatUser;
